import api from './api';

// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// Create api instance for calendar entries
// We can't just use api because calendarService prefixes everything with /calendar
// So we'll use the main api instance but prepend /calendar to requests, OR
// create a new instance that INHERITS the interceptors.
// Easiest way to inherit interceptors is to just use the main api instance and modify the methods to prepend /calendar
// OR just use api and change the calls.
// Let's create a proxy wrapper or just use api with a baseURL override if axios supports it (it doesn't easily inherit interceptors on new create).

// Best approach: Use `api` directly, but we need to verify if we can overwrite baseURL per request or just prepend.
// Prepending is safer.

const calendarAPIInstance = {
  get: (url, config) => api.get(`/calendar${url}`, config),
  post: (url, data, config) => api.post(`/calendar${url}`, data, config),
  put: (url, data, config) => api.put(`/calendar${url}`, data, config),
  delete: (url, config) => api.delete(`/calendar${url}`, config),
  interceptors: api.interceptors // Expose interceptors just in case
};

// Visit Type API calls
export const visitTypeAPI = {
  // Get all visit types
  getVisitTypes: async () => {
    try {
      const response = await calendarAPIInstance.get('/visit-types');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch visit types');
    }
  },

  // Get visit types for calendar dropdown (scoped to a practice — matches Visit Type screen)
  getCalendarVisitTypes: async (practiceId) => {
    try {
      const params = {};
      if (practiceId != null && practiceId !== '') {
        params.practice_id = practiceId;
      }
      const response = await calendarAPIInstance.get('/calendar-visit-types', { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch calendar visit types');
    }
  },

  // Get practices for dropdown
  getPractices: async () => {
    try {
      const response = await calendarAPIInstance.get('/visit-types/practices');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch practices');
    }
  },

  // Get visit type by ID
  getVisitType: async (visitTypeId) => {
    try {
      const response = await calendarAPIInstance.get(`/visit-types/${visitTypeId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch visit type');
    }
  },

  // Create visit type
  createVisitType: async (visitTypeData) => {
    try {
      const response = await calendarAPIInstance.post('/visit-types', visitTypeData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create visit type');
    }
  },

  // Update visit type
  updateVisitType: async (visitTypeId, visitTypeData) => {
    try {
      const response = await calendarAPIInstance.put(`/visit-types/${visitTypeId}`, visitTypeData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update visit type');
    }
  },

  // Delete visit type
  deleteVisitType: async (visitTypeId) => {
    try {
      const response = await calendarAPIInstance.delete(`/visit-types/${visitTypeId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete visit type');
    }
  }
};

// Working Hours API calls
export const workingHoursAPI = {
  // Get working hours form data
  getWorkingHours: async () => {
    try {
      const response = await calendarAPIInstance.get('/working-hours');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch working hours');
    }
  },

  // Validate working hours
  validateWorkingHours: async (data) => {
    try {
      const response = await calendarAPIInstance.post('/working-hours/validate', data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to validate working hours');
    }
  },

  // Store working hours
  storeWorkingHours: async (data) => {
    try {
      const response = await calendarAPIInstance.post('/working-hours', data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to save working hours');
    }
  },

  // Remove availability slot
  removeAvailabilitySlot: async (slotId) => {
    try {
      const response = await calendarAPIInstance.post('/working-hours/remove-slot', { id: slotId });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to remove availability slot');
    }
  },

  // Get providers and facilities
  getProvidersAndFacility: async (practiceId) => {
    try {
      const response = await calendarAPIInstance.get(`/providers-and-facility/${practiceId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch providers and facilities');
    }
  }
};

// Calendar API calls
export const calendarAPI = {
  // Get calendar view data
  getCalendarView: async () => {
    try {
      const response = await calendarAPIInstance.get('/view');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch calendar view');
    }
  },

  // Get caregivers, patients, visit types for practice
  getSystemCaregiversOfPracticeAndPatient: async (practiceId, caregiverId = null) => {
    try {
      const params = { practiceId };
      if (caregiverId != null && caregiverId !== '') {
        params.caregiverId = caregiverId;
      }
      const response = await calendarAPIInstance.get('/caregivers-by-practice', {
        params
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch data');
    }
  },

  // Get appointment time schedule
  getAppointmentTimeSchedule: async (date, timeInterval) => {
    try {
      const response = await calendarAPIInstance.get('/appointment-time', {
        params: { date, time_interval: timeInterval }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to get appointment time schedule');
    }
  },

  // Generate time interval
  generateTimeInterval: async (timeInterval) => {
    try {
      const response = await calendarAPIInstance.post('/generate-time-interval', {
        time_interval: timeInterval
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to generate time interval');
    }
  },

  // Get actual working hours for a specific date
  getActualWorkingHours: async (date) => {
    try {
      const params = {};
      if (date) params.date = date;
      const response = await calendarAPIInstance.get('/actual-working-hours', { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to get working hours');
    }
  },

  // Get slots
  getSlots: async (date, timeDifference) => {
    try {
      const response = await calendarAPIInstance.get(`/slots/${date}/${timeDifference}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to get slots');
    }
  },

  // Get visit types (for calendar)
  getVisitTypes: async () => {
    try {
      const response = await calendarAPIInstance.get('/visit-types');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch visit types');
    }
  },

  // Fetch practices
  fetchPractice: async (caregiverId = null) => {
    try {
      const params = {};
      if (caregiverId != null && caregiverId !== '') {
        params.caregiver_id = caregiverId;
      }
      const response = await calendarAPIInstance.get('/practices', { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch practices');
    }
  },

  // Get providers
  getProviders: async (search = '', page = 1) => {
    try {
      const response = await calendarAPIInstance.get('/providers', {
        params: { search, page }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch providers');
    }
  },

  // Get practice caregivers
  getPracticeCaregivers: async () => {
    try {
      const response = await calendarAPIInstance.get('/practice-caregivers');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch practice caregivers');
    }
  },

  // Update selected caregivers
  updateSelectedCaregivers: async (caregiverIds, userId) => {
    try {
      const response = await calendarAPIInstance.post('/selected-caregivers', {
        caregiver_ids: caregiverIds,
        userId
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update selected caregivers');
    }
  },

  // Get selected caregivers
  getSelectedCaregivers: async (userId) => {
    try {
      const response = await calendarAPIInstance.get(`/selected-caregivers/${userId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch selected caregivers');
    }
  },

  // Check permission
  checkPermission: async (userId, permission) => {
    try {
      const response = await calendarAPIInstance.get('/check-permission', {
        params: { userId, permission }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to check permission');
    }
  },

  // Get appointments
  getAppointments: async (startDate, endDate, caregiverIds, practiceId) => {
    try {
      const params = {
        start_date: startDate,
        end_date: endDate
      };
      if (caregiverIds && caregiverIds.length > 0) {
        params.caregiver_ids = caregiverIds.join(',');
      }
      if (practiceId) {
        params.practice_id = practiceId;
      }
      // Note: baseURL already includes /calendar, so use /appointments not /calendar/appointments
      const response = await calendarAPIInstance.get('/appointments', { params });
      // Ensure response has success and appointments fields
      if (response && response.data) {
        return {
          success: response.data.success !== false,
          appointments: response.data.appointments || response.data.data || []
        };
      }
      return { success: false, appointments: [] };
    } catch (error) {
      // Return empty result instead of throwing to prevent UI errors
      return {
        success: false,
        appointments: [],
        error: error.response?.data?.message || error.message || 'Failed to fetch appointments'
      };
    }
  },

  // Create appointment
  createAppointment: async (appointmentData) => {
    try {
      const response = await calendarAPIInstance.post('/appointments', appointmentData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create appointment');
    }
  },

  // Update appointment
  updateAppointment: async (appointmentId, appointmentData) => {
    try {
      const response = await calendarAPIInstance.put(`/appointments/${appointmentId}`, appointmentData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update appointment');
    }
  },

  // Delete appointment
  deleteAppointment: async (appointmentId) => {
    try {
      const response = await calendarAPIInstance.delete(`/appointments/${appointmentId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete appointment');
    }
  }
};

const calendarService = {
  visitTypeAPI,
  workingHoursAPI,
  calendarAPI
};

export default calendarService;

